import { useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * Hook to check user permissions based on their role
 * 
 * Current permissions:
 * - root: Can manage events, alerts, feedback, jobs, and all admin operations
 * - account-admin: Same permissions as collaborator (can send/view feedback, read-only otherwise)
 * - collaborator: Can send/view feedback, read-only access to all other features
 * - developer: Same permissions as collaborator
 * - viewer: Read-only access
 * 
 * Usage:
 *   const { canManageEvents, isRoot } = usePermissions();
 *   
 *   if (canManageEvents) {
 *     // Show event management UI
 *   }
 */
export const usePermissions = () => {
  const { user } = useAuth();

  const isRoot = useMemo(() => {
    return user?.role === 'root';
  }, [user]);

  const canManageEvents = useMemo(() => {
    // Currently only root users can manage events
    return isRoot;
  }, [isRoot]);

  return {
    canManageEvents,
    isRoot,
    userRole: user?.role,
  };
};

